import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/auth.service';

// Define user type
export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  avatar?: string;
}

// Define auth context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => void;
}

// Define register data type
export interface RegisterData {
  email: string;
  fullname: string;
  phone: string;
  password: string;
  dob: string;
  gender: 'male' | 'female' | 'other';
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user data
const MOCK_USER: User = {
  id: '1',
  name: 'Nguyễn Văn A',
  email: 'test@example.com',
  avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check for saved auth on mount
  useEffect(() => {
    const checkAuth = () => {
      const savedUser = localStorage.getItem('fruitshop_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Login function (mock API)
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      // // Simulate API call
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      // // Simple validation (in real app, this would be a server call)
      // if (email === 'test@example.com' && password === 'password123') {
      //   setUser(MOCK_USER);
      //   localStorage.setItem('fruitshop_user', JSON.stringify(MOCK_USER));
      //   return true;
      // }
      // return false;
      const response = await authService.login(email, password);
      if (response) {
        setUser(response.user);
        localStorage.setItem('fruitshop_user', JSON.stringify(response.user));
        localStorage.setItem('auth_token', response.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Register function - not directly used anymore, we call authService in the Register component
  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      await authService.register(userData);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('fruitshop_user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
